import { useState, useCallback } from 'react';
import { ValidationResult, validateEmail, validateURL, validatePort, validateIP, validateProjectName, validateROSTopic, validateNumber } from 'src/utils/validation';

export interface UseValidationOptions {
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
}

export const useValidation = <T extends Record<string, any>>(
  initialValues: T,
  validators: Partial<Record<keyof T, (value: any) => ValidationResult>>,
  options: UseValidationOptions = {}
) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});

  const validateField = useCallback(
    (field: keyof T, value: any): ValidationResult => {
      const validator = validators[field];
      if (!validator) {
        return { isValid: true };
      }
      return validator(value);
    },
    [validators]
  );

  const validateAll = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    Object.keys(validators).forEach((field) => {
      const fieldKey = field as keyof T;
      const result = validateField(fieldKey, values[fieldKey]);
      if (!result.isValid) {
        newErrors[fieldKey] = result.error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [values, validators, validateField]);

  const handleChange = useCallback(
    (field: keyof T) => (value: any) => {
      setValues((prev) => ({ ...prev, [field]: value }));

      if (options.validateOnChange) {
        const result = validateField(field, value);
        if (result.isValid) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        } else {
          setErrors((prev) => ({ ...prev, [field]: result.error }));
        }
      }
    },
    [options.validateOnChange, validateField]
  );

  const handleBlur = useCallback(
    (field: keyof T) => () => {
      setTouched((prev) => ({ ...prev, [field]: true }));

      if (options.validateOnBlur) {
        const result = validateField(field, values[field]);
        if (result.isValid) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
          });
        } else {
          setErrors((prev) => ({ ...prev, [field]: result.error }));
        }
      }
    },
    [options.validateOnBlur, validateField, values]
  );

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    setValues,
    setErrors,
    validateField,
    validateAll,
    handleChange,
    handleBlur,
    reset,
    isValid: Object.keys(errors).length === 0,
  };
};

// Export validation functions for convenience
export { validateEmail, validateURL, validatePort, validateIP, validateProjectName, validateROSTopic, validateNumber };

